import { toast } from "react-toastify";
import { CompleteCompanyRegistrationResponse } from "./types";
import { RegistrationFormValues } from "../pages/Registration/registration.schema";

export const useAuthApi = () => {
  const registerUser = async (
    data: RegistrationFormValues,
    showToast = true,
  ): Promise<{ token: string }> => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/auth/register`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    );

    if (!res.ok) {
      const errorText = await res.text();
      if (showToast) toast.error(errorText || "Failed to register");
      throw new Error(errorText || "Failed to register");
    }

    const result = await res.json();
    if (showToast) toast.success("Registration successful");
    return result;
  };

  const validateRegistrationData = async (
    data: RegistrationFormValues,
  ): Promise<{ valid: boolean; errors?: string[] }> => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/auth/validate-registration`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    );

    const result = await res.json().catch(() => ({
      valid: false,
      errors: ["Invalid response from server"],
    }));

    if (!res.ok || !result.valid) {
      return { valid: false, errors: result.errors || ["Validation failed"] };
    }

    return result;
  };

  const startCompanyRegistration = async (draft: RegistrationFormValues) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/auth/start-company-registration`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      },
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<{ url: string }>;
  };

  // NEW: step 2 – complete company registration on success page
  const completeCompanyRegistration = async (
    sessionId: string,
  ): Promise<CompleteCompanyRegistrationResponse> => {
    const res = await fetch(
      `${
        process.env.REACT_APP_API_URL
      }/api/auth/complete-company-registration?session_id=${encodeURIComponent(
        sessionId,
      )}`,
      { method: "POST" },
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const forgotPassword = async (
    payload: { email?: string; phone?: string },
    showToast = true,
  ): Promise<void> => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/auth/forgot-password`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );

    if (!res.ok) {
      const errorText = await res.text();
      if (showToast)
        toast.error(errorText || "Failed to send reset instructions");
      throw new Error(errorText || "Failed to send reset instructions");
    }

    if (showToast) toast.success("Reset instructions sent");
  };

  const resetPassword = async (
    token: string,
    newPassword: string,
    showToast = true,
  ): Promise<void> => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/auth/reset-password`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      },
    );
    const msg = await res.text();
    if (!res.ok) {
      if (showToast) toast.error(msg || "Failed to reset password");
      throw new Error(msg || "Failed to reset password");
    }
    if (showToast) toast.success(msg || "Password has been reset.");
  };

  const loginUser = async (
    email: string,
    password: string,
    showToast = true,
  ): Promise<{ token: string; emailNotVerified?: boolean }> => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const message = await res.text();
      if (message === "EMAIL_NOT_VERIFIED") {
        if (showToast)
          toast.error(
            "Email not verified. Please verify your email before logging in.",
          );

        return { token: "", emailNotVerified: true };
      }
      if (showToast) toast.error(message || "Login failed");
      throw new Error(message || "Login failed");
    }

    const result = await res.json();
    if (showToast) toast.success("Logged in successfully");
    return result;
  };

  const resendVerification = async (email: string) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/auth/resend-verification`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      },
    );

    const text = await res.text();
    if (!res.ok) throw new Error(text || "Failed to resend verification email");
    return text;
  };

  const verifyEmail = async (token: string, showToast = true) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/auth/verify-email`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      },
    );

    const message = await res.text();

    if (!res.ok) {
      throw new Error(message || "Verification failed");
    }

    if (showToast) {
      toast.success(message || "Email verified. Please sign in.");
    }

    return { message };
  };

  return {
    registerUser,
    validateRegistrationData,
    startCompanyRegistration,
    completeCompanyRegistration,
    forgotPassword,
    resetPassword,
    loginUser,
    resendVerification,
    verifyEmail,
  };
};
